import { useState } from "react";
import { ArrowRight } from "lucide-react";
import Wordmark from "./Wordmark";

const QUESTIONS = [
  {
    id: 1,
    text: "How long does it take you to write a single LinkedIn post from scratch?",
    options: [
      { label: "A", text: "15–30 minutes" },
      { label: "B", text: "30–60 minutes" },
      { label: "C", text: "1–2 hours" },
      { label: "D", text: "I avoid writing them altogether" },
    ],
  },
  {
    id: 2,
    text: "How many YouTube videos do you have that you've never repurposed into content?",
    options: [
      { label: "A", text: "Just a few" },
      { label: "B", text: "10–20 videos" },
      { label: "C", text: "20–50 videos" },
      { label: "D", text: "Way too many to count" },
    ],
  },
  {
    id: 3,
    text: "How much do you currently spend on content creation per month?",
    options: [
      { label: "A", text: "Nothing — I do it all myself" },
      { label: "B", text: "£100–£500 on tools or freelancers" },
      { label: "C", text: "£500–£2,000" },
      { label: "D", text: "£2,000+" },
    ],
  },
  {
    id: 4,
    text: "How often do you manage to post on LinkedIn or your blog?",
    options: [
      { label: "A", text: "Daily" },
      { label: "B", text: "A few times a week" },
      { label: "C", text: "Once a week or less" },
      { label: "D", text: "Rarely — I just can't keep up" },
    ],
  },
];

function calculateResults(answers) {
  const timeSaved = {
    A: { value: "2+ hours", detail: "You're spending ~2 hours/week just writing LinkedIn posts. YouSift handles that instantly." },
    B: { value: "4+ hours", detail: "At 30–60 min per post, you're losing 4+ hours a week to content that could be automated." },
    C: { value: "7+ hours", detail: "Writing posts at 1–2 hours each is costing you nearly a full workday every week." },
    D: { value: "Countless hours", detail: "Avoiding LinkedIn entirely means missed reach. YouSift removes the friction." },
  };

  const contentLeft = {
    A: { value: "20+ pieces", detail: "Even a handful of videos can generate 20+ pieces of content — LinkedIn posts, blog articles, and more." },
    B: { value: "80+ pieces", detail: "Up to 20 videos sitting idle. That's potentially 80+ pieces of content waiting to be published." },
    C: { value: "200+ pieces", detail: "Up to 50 videos in your library. That's 200+ pieces of content you haven't published yet." },
    D: { value: "500+ pieces", detail: "Your video library is a content goldmine. YouSift can start working through it today." },
  };

  const moneySaved = {
    A: { value: "Your time", detail: "You're doing it all yourself — but your time has value. YouSift gives it back." },
    B: { value: "Up to £500/mo", detail: "You're spending up to £500/month on content. YouSift costs a fraction of that." },
    C: { value: "Up to £2,000/mo", detail: "You're spending up to £2,000/month on content. YouSift can handle the bulk of it for less." },
    D: { value: "£2,000+/mo", detail: "At £2,000+/month, your content spend is significant. YouSift can dramatically reduce it." },
  };

  return {
    timeSaved: timeSaved[answers[0]] || timeSaved.C,
    contentLeft: contentLeft[answers[1]] || contentLeft.C,
    moneySaved: moneySaved[answers[2]] || moneySaved.B,
  };
}

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:8000";

export default function OnboardingQuiz({ userId, onComplete }) {
  const [step, setStep] = useState(0); // 0 = intro, 1-4 = questions, 5 = results
  const [answers, setAnswers] = useState([]);
  const [selected, setSelected] = useState(null);

  const currentQuestion = QUESTIONS[step - 1];
  const results = step === 5 ? calculateResults(answers) : null;

  const handleSelect = (label) => setSelected(label);

  const handleNext = () => {
    if (step === 0) {
      setStep(1);
      return;
    }
    if (selected === null) return;
    const newAnswers = [...answers, selected];
    setAnswers(newAnswers);
    setSelected(null);
    setStep(step + 1);
  };

  const handleComplete = () => {
    localStorage.setItem("yousift_onboarding_complete", "true");
    const answerMap = { q1: answers[0], q2: answers[1], q3: answers[2], q4: answers[3] };
    fetch(`${API_BASE}/api/quiz-complete`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ user_id: userId, answers: answerMap }),
    }).catch(() => {});
    onComplete();
  };

  return (
    <div className="fixed inset-0 bg-[#0F0F0F] z-50 flex flex-col items-center justify-center px-4">
      {/* Wordmark */}
      <div className="absolute top-8 left-1/2 -translate-x-1/2">
        <Wordmark size="sm" darkBg={true} />
      </div>

      {/* Intro screen */}
      {step === 0 && (
        <div className="w-full max-w-lg text-center">
          <h1 className="text-3xl md:text-4xl font-black text-white mb-3">
            Let's see how much content you're sitting on.
          </h1>
          <p className="text-gray-400 text-lg mb-10">4 quick questions. No right answers.</p>
          <button
            onClick={handleNext}
            className="w-full py-4 bg-[#FF0000] hover:bg-[#CC0000] text-white font-black text-lg rounded-xl transition-all duration-200 flex items-center justify-center gap-3"
          >
            Let's go <ArrowRight size={20} />
          </button>
        </div>
      )}

      {/* Question screens */}
      {step >= 1 && step <= 4 && (
        <div className="w-full max-w-lg">
          {/* Progress */}
          <div className="mb-8">
            <div className="flex justify-between text-xs text-gray-500 mb-2">
              <span>Question {step} of 4</span>
              <span>{step * 25}%</span>
            </div>
            <div className="w-full h-1 bg-[#2A2A2A] rounded-full">
              <div
                className="h-full bg-[#FF0000] rounded-full transition-all duration-500"
                style={{ width: `${step * 25}%` }}
              />
            </div>
          </div>

          {/* Question */}
          <h2 className="text-xl md:text-2xl font-bold text-white mb-6">
            {currentQuestion.text}
          </h2>

          {/* Options */}
          <div className="flex flex-col gap-3 mb-8">
            {currentQuestion.options.map((opt) => (
              <button
                key={opt.label}
                onClick={() => handleSelect(opt.label)}
                className={`w-full text-left px-5 py-4 rounded-xl border transition-all duration-150 flex items-center gap-4 ${
                  selected === opt.label
                    ? "border-[#FF0000] bg-[#FF0000]/10 text-white"
                    : "border-[#2A2A2A] bg-[#1A1A1A] text-gray-300 hover:border-[#444]"
                }`}
              >
                <span
                  className={`w-8 h-8 rounded-lg flex items-center justify-center text-sm font-black shrink-0 ${
                    selected === opt.label ? "bg-[#FF0000] text-white" : "bg-[#2A2A2A] text-gray-400"
                  }`}
                >
                  {opt.label}
                </span>
                <span className="font-medium">{opt.text}</span>
              </button>
            ))}
          </div>

          <button
            onClick={handleNext}
            disabled={selected === null}
            className="w-full py-4 bg-[#FF0000] hover:bg-[#CC0000] disabled:opacity-30 disabled:cursor-not-allowed text-white font-black text-lg rounded-xl transition-all duration-200 flex items-center justify-center gap-3"
          >
            {step === 4 ? "See my results" : "Next"} <ArrowRight size={20} />
          </button>
        </div>
      )}

      {/* Results screen */}
      {step === 5 && results && (
        <div className="w-full max-w-lg text-center">
          <h2 className="text-2xl md:text-3xl font-black text-white mb-2">
            Here's what YouSift can give you back.
          </h2>
          <p className="text-gray-500 text-sm mb-8">Based on your answers</p>

          <div className="flex flex-col gap-4 mb-8 text-left">
            {/* Stat 1 */}
            <div className="bg-[#1A1A1A] border border-[#2A2A2A] rounded-xl px-5 py-4">
              <p className="text-xs text-gray-500 uppercase tracking-widest mb-1">Time saved per week</p>
              <p className="text-2xl font-black text-[#FF0000] mb-1">{results.timeSaved.value}</p>
              <p className="text-gray-400 text-sm">{results.timeSaved.detail}</p>
            </div>

            {/* Stat 2 */}
            <div className="bg-[#1A1A1A] border border-[#2A2A2A] rounded-xl px-5 py-4">
              <p className="text-xs text-gray-500 uppercase tracking-widest mb-1">Content you're leaving on the table</p>
              <p className="text-2xl font-black text-[#FF0000] mb-1">{results.contentLeft.value}</p>
              <p className="text-gray-400 text-sm">{results.contentLeft.detail}</p>
            </div>

            {/* Stat 3 */}
            <div className="bg-[#1A1A1A] border border-[#2A2A2A] rounded-xl px-5 py-4">
              <p className="text-xs text-gray-500 uppercase tracking-widest mb-1">Money you could redirect</p>
              <p className="text-2xl font-black text-[#FF0000] mb-1">{results.moneySaved.value}</p>
              <p className="text-gray-400 text-sm">{results.moneySaved.detail}</p>
            </div>
          </div>

          <button
            onClick={handleComplete}
            className="w-full py-4 bg-[#FF0000] hover:bg-[#CC0000] text-white font-black text-lg rounded-xl transition-all duration-200 flex items-center justify-center gap-3"
          >
            Let's get started <ArrowRight size={20} />
          </button>
        </div>
      )}
    </div>
  );
}
